import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Divider, Group, MultiSelect, Popover } from "@mantine/core";
import { getHardcodedColor } from "../../utils";
import { nodeTypes } from "../../types";
import { getAllPrimekgIndications } from "../../services/indication";
import { rankings, rankItem } from "@tanstack/match-sorter-utils";
import UserSelectionContext from "../../providers/UserSelectionProvider";
import { getMoaGenes } from "../../services/moas";
import { notifications } from "@mantine/notifications";

interface QueryFocusProps {
    data: [string, number][];
    label: string;
    type: nodeTypes;
    selectedData: string[];
    onSelectChange: (data: string[]) => void;
}

const QueryFocus: React.FC<QueryFocusProps> = ({
    data,
    label,
    type,
    onSelectChange,
    selectedData
}) => {
    const [opened, setOpened] = useState(false);

    const sortedOptions = useMemo(() => {
        const selectedSet = new Set(selectedData);

        const selectedItems = data
            .filter(([label]) => selectedSet.has(label))
            .sort((a, b) => b[1] - a[1])
            .map(([label]) => ({ label, value: label }));

        const nonSelectedItems = data
            .filter(([label]) => !selectedSet.has(label))
            .sort((a, b) => b[1] - a[1])
            .map(([label]) => ({ label, value: label }));

        return [...selectedItems, ...nonSelectedItems];
    }, [data, selectedData]);

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            closeOnClickOutside={true}
            width={300}
            position="bottom-start"
            withArrow
            shadow="md"
        >
            <Popover.Target>
                <Button
                    onClick={() => setOpened((o) => !o)}
                    color={getHardcodedColor(type)}
                >
                    {label}
                </Button>
            </Popover.Target>

            <Popover.Dropdown>
                <MultiSelect
                    data={sortedOptions}
                    value={selectedData}
                    onChange={(values) => {
                        onSelectChange(values);
                        setOpened(true);
                    }}
                    placeholder="Search..."
                    searchable
                    dropdownOpened={true}
                    limit={50}
                    styles={{
                        pill: {
                            display: 'none'
                        }
                    }}
                />
            </Popover.Dropdown>
        </Popover>
    );
};

interface QuerySummaryProps {
    onSubmit: (query: { genes: string[], indications: string[] }) => void;
}

const normalize = (str: string) =>
    str.toLowerCase()
        .replace(/[^\w\s]/g, "")
        .split(/\s+/)
        .sort()
        .join(" ");

const fuzzySearch = (text, filterValue) => {
    if (!filterValue) return [true, 0];

    const normalizedText = normalize(text);
    const normalizedFilter = normalize(filterValue);

    const itemRank = rankItem(normalizedText, normalizedFilter, {
        threshold: rankings.MATCHES,
    });

    return itemRank.passed ? [true, itemRank.rank] : [false, itemRank.rank];
};

const QuerySummary: React.FC<QuerySummaryProps> = ({ onSubmit }) => {
    const { focusedIndications, selectedMoa } = useContext(UserSelectionContext)
    const [selectedGenes, setSelectedGenes] = useState<string[]>([]);
    const [selectedIndications, setSelectedIndications] = useState<string[]>(null);
    const [rankedIndications, setRankedIndications] = useState<[string, number][]>([])
    const [moaGenes, setMoaGenes] = useState<string[]>([])

    useEffect(() => {
        const callGetPrimekgIndications = async () => {
            const response = await getAllPrimekgIndications()

            const searchResults = response.map(name => {
                const [passed, rank] = fuzzySearch(name, focusedIndications) as [boolean, number];
                return [name, passed, rank];
            });

            const topMatch = searchResults.filter(d => d[2] >= 2)
            if (topMatch.length === 0) {
                notifications.show({
                    title: 'No Matches in PrimeKG',
                    message: "The auto-search couldn't find any matches in PrimeKG for the checked indication. Please click the indication button in the query and select the matches manually.",
                    color: 'red',
                    position: 'top-center',
                    autoClose: 15000,
                })
            }
            setSelectedIndications(topMatch.map(d => d[0]))
            setRankedIndications(searchResults.map(d => [d[0], d[2]]))
        }
        setSelectedIndications(null)
        callGetPrimekgIndications()
    }, [focusedIndications])

    useEffect(() => {
        const callGetMoaGenes = async () => {
            const response = await getMoaGenes(selectedMoa)
            setMoaGenes(response)
            setSelectedGenes(response)
        }
        setSelectedGenes([])
        callGetMoaGenes()
    }, [selectedMoa])

    return (
        <Group>
            <QueryFocus
                label={selectedMoa}
                data={moaGenes.map(g => [g, 1])}
                type={nodeTypes.moa}
                onSelectChange={setSelectedGenes}
                selectedData={selectedGenes}
            />
            <Divider label={'Shortest Paths'}
                labelPosition="center"
                style={{ flexGrow: 1, minWidth: 200 }}
            />
            <QueryFocus
                label={focusedIndications}
                data={rankedIndications}
                type={nodeTypes.disease}
                onSelectChange={setSelectedIndications}
                selectedData={selectedIndications}
            />
            <Button
                loading={selectedIndications === null}
                disabled={!selectedGenes.length || selectedIndications === null || !selectedIndications.length}
                variant="transparent"
                onClick={() => {
                    onSubmit({ genes: selectedGenes, indications: selectedIndications })
                }}
            >
                Submit
            </Button>
        </Group>
    );
}

export default QuerySummary;
